import { useRef } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useListContext } from "react-admin";
import { colors, radius } from "../data/tokens";

// ============================================================
// Navegação compacta de página ("1–20 de 62  ‹  ›"), estilo
// caixa de e-mail, para o topo direito das tabelas.
// - <TablePageIndicator />: versão pura, controlada por props —
//   serve para telas com paginação própria (ex.: telas Iugu).
// - <ListPageIndicator />: versão ligada ao contexto de lista do
//   react-admin (usar dentro de <List>, ex.: na toolbar de ações).
// ============================================================

type TablePageIndicatorProps = {
  from: number;
  to: number;
  total?: number | null; // omita/null quando a API não informa o total
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
};

const arrowSx = {
  width: 30,
  height: 30,
  borderRadius: `${radius.sm}px`,
  color: colors.primary,
  "&.Mui-disabled": { opacity: 0.35 },
};

export function TablePageIndicator({
  from,
  to,
  total,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
}: TablePageIndicatorProps) {
  const fmt = (n: number) => n.toLocaleString("pt-BR");
  const label =
    typeof total === "number"
      ? `${fmt(from)}–${fmt(to)} de ${fmt(total)}`
      : `${fmt(from)}–${fmt(to)}`;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
      <Typography
        sx={{
          fontSize: 12.5,
          color: colors.textMuted,
          whiteSpace: "nowrap",
          marginRight: 0.75,
        }}
      >
        {label}
      </Typography>
      <Tooltip title="Página anterior">
        <span>
          <IconButton
            size="small"
            onClick={onPrev}
            disabled={!hasPrev}
            sx={arrowSx}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Próxima página">
        <span>
          <IconButton
            size="small"
            onClick={onNext}
            disabled={!hasNext}
            sx={arrowSx}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

// Versão para listas do react-admin: lê página/total do contexto.
export function ListPageIndicator() {
  const { page, perPage, total, setPage } = useListContext();
  // Guarda o último total conhecido: durante a troca de página o total
  // fica indefinido por um instante e o indicador sumia/voltava,
  // fazendo a toolbar "piscar"
  const lastTotalRef = useRef<number | null>(null);
  if (typeof total === "number") {
    lastTotalRef.current = total;
  }
  const knownTotal =
    typeof total === "number" ? total : lastTotalRef.current;
  if (typeof knownTotal !== "number" || knownTotal <= 0) return null;
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, knownTotal);
  return (
    <TablePageIndicator
      from={from}
      to={to}
      total={knownTotal}
      hasPrev={page > 1}
      hasNext={to < knownTotal}
      onPrev={() => setPage(page - 1)}
      onNext={() => setPage(page + 1)}
    />
  );
}
